const BASE_URL = process.env.PROXY_BASE_URL || process.env.VITE_TEAMHAVEN_BASE_URL;
const API_KEY = process.env.PROXY_API_KEY || process.env.VITE_TEAMHAVEN_API_KEY;

async function fetchWithRetry(url: string, options: RequestInit = {}, maxRetries = 3): Promise<Response> {
  let attempt = 0;
  while (true) {
    try {
      attempt++;
      const response = await fetch(url, options);
      
      if (response.ok) {
        return response;
      }
      
      const shouldRetry = response.status === 429 || (response.status >= 500 && response.status <= 599);
      if (!shouldRetry || attempt > maxRetries) {
        return response;
      }
      
      let delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
      
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        if (retryAfter) {
          const seconds = parseInt(retryAfter, 10);
          if (!isNaN(seconds)) {
            delay = seconds * 1000;
          }
        }
      }
      
      console.warn(`[TH Sync Retry] Attempt ${attempt} failed with status ${response.status}. Retrying in ${Math.round(delay)}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    } catch (error) {
      if (attempt > maxRetries) {
        throw error;
      }
      const delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
      console.warn(`[TH Sync Retry] Attempt ${attempt} failed with network error. Retrying in ${Math.round(delay)}ms...`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

export async function fetchActiveTeamHavenProjects(): Promise<any[]> {
  let allProjects: any[] = [];
  let skip = 0;
  let take = 500;
  let hasMore = true;

  try {
    while (hasMore) {
      const response = await fetchWithRetry(`${BASE_URL}/projects?Skip=${skip}&Take=${take}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'x-api-key': API_KEY
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      const items = data.Items || [];
      allProjects = allProjects.concat(items);
      hasMore = !!data.More;
      skip += items.length;

      if (items.length === 0) hasMore = false;
    }
    return allProjects;
  } catch (error) {
    console.error('Error fetching TeamHaven projects:', error);
    throw error;
  }
}

export async function createTeamHavenProject(projectData: { Name: string, Description: string, Reference: string | null }): Promise<{ ProjectID: number }> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(projectData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating TeamHaven project:', error);
    throw error;
  }
}

export async function getAllProjectQuestionnaires(projectId: number): Promise<any[]> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects/${projectId}/questionnaires`, {
      method: 'GET',
      headers: {
        'x-api-key': API_KEY
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('[TeamHaven] Questionnaires response:', data);

    if (Array.isArray(data)) return data;
    if (data.Items && Array.isArray(data.Items)) return data.Items;
    if (data.data && Array.isArray(data.data)) return data.data;

    return [];
  } catch (error) {
    console.error('Error fetching TeamHaven questionnaires:', error);
    throw error;
  }
}

export async function getProjectQuestionnaire(projectId: number, questionnaireId: number = 1): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects/${projectId}/questionnaires/${questionnaireId}`, {
      method: 'GET',
      headers: {
        'x-api-key': API_KEY
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching TeamHaven questionnaire:', error);
    throw error;
  }
}

export async function updateProjectQuestionnaire(projectId: number, questionnaireId: number, design: any): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects/${projectId}/questionnaires/${questionnaireId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(design)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating TeamHaven questionnaire:', error);
    throw error;
  }
}

export async function createProjectTarget(projectId: number, targetData: { Name: string, Details?: string, Attributes: any }): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects/${projectId}/targets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(targetData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating TeamHaven target:', error);
    throw error;
  }
}

export async function createTeamHavenCall(projectId: number, targetId: number, data: any): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/projects/${projectId}/targets/${targetId}/calls`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating TeamHaven call:', error);
    throw error;
  }
}

export async function updateTeamHavenCall(callId: number, data: any): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating TeamHaven call:', error);
    throw error;
  }
}

export async function assignTeamHavenCall(callId: number, userId: number): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'x-api-key': API_KEY
      },
      body: String(userId)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error assigning TeamHaven call:', error);
    throw error;
  }
}

export async function removeTeamHavenCallAssignment(callId: number): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}/user`, {
      method: 'DELETE',
      headers: {
        'x-api-key': API_KEY
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error removing TeamHaven call assignment:', error);
    throw error;
  }
}

export async function activateTeamHavenCall(callId: number): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}/commands/activate`, {
      method: 'PUT',
      headers: {
        'x-api-key': API_KEY,
        'Content-Length': '0'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error activating TeamHaven call:', error);
    throw error;
  }
}

export async function deactivateTeamHavenCall(callId: number): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}/commands/deactivate`, {
      method: 'PUT',
      headers: {
        'x-api-key': API_KEY,
        'Content-Length': '0'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error deactivating TeamHaven call:', error);
    throw error;
  }
}

export async function deleteTeamHavenCall(callId: number): Promise<any> {
  try {
    const response = await fetchWithRetry(`${BASE_URL}/calls/${callId}`, {
      method: 'DELETE',
      headers: {
        'x-api-key': API_KEY
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TeamHaven error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error deleting TeamHaven call:', error);
    throw error;
  }
}
